// Loan Simulator – Belgium
// Implements the methodology of the Belgian reference model (version 2022-12):
// borrowing capacity from the monthly instalment, notary fees, regional contribution,
// one-off and periodic costs, repayment schedule, TAEG and totals.

import { int, round } from "../components/simulatorRounding";

// ---------------------------------------------------------------------------
// Parameters set by the administrator (updated every half-year).
// TODO: load these from the backend once parameter versions are stored there.
// ---------------------------------------------------------------------------
export const BELGIUM_LOAN_PARAMS = {
  version: "Parameters: second half of 2026",
  monthlyInterestRate: 0.003955, // 0.3955 % per month
  valuationFee: 250,
  fileFee: 250,
  bankAccountFee: { premium: 0, frequency: "annual" },
  extraCosts: 0,
  // Notary tariff table: one row per year, the most recent row is used.
  notaryTariffs: [
    {
      year: 2024,
      accessoriesRate: 0.05,
      registrationDutyRate: 0.01,
      mortgageDutyRate: 0.003,
      retributionMin: 270,
      retributionMax: 985,
      retributionThreshold: 300000,
      administrativeFee: 1117,
      writingFee: 100,
      vatRate: 0.21,
      annexFee: 100,
    },
  ],
};

// Fixed rules (change only if the law changes)
export const REGIONS = {
  flanders: { regionalContributionRate: 0.002, minimumAccessories: 1250 },
  wallonia: { regionalContributionRate: 0.002, minimumAccessories: 3750 },
  brussels: { regionalContributionRate: 0, minimumAccessories: 1250 },
};

export const FREQUENCY_MONTHS = { monthly: 1, quarterly: 3, annual: 12 };
export const FREQUENCIES = ["one-off", "monthly", "quarterly", "annual"];
export const MIN_TERM_YEARS = 1;
export const MAX_TERM_YEARS = 32;

// ---------------------------------------------------------------------------
// Rounding, as in the reference model
// ---------------------------------------------------------------------------
export { int, round };

const rd9 = (x) => round(x, 9);

// ---------------------------------------------------------------------------
// Calculation
// ---------------------------------------------------------------------------

function latestTariff(params) {
  return params.notaryTariffs.reduce((a, b) => (b.year > a.year ? b : a));
}

// Step 1 – borrowing capacity from the instalment
export function borrowingCapacity(instalment, n, i) {
  if (i === 0) return round(instalment * n, 2);
  return round(instalment * ((1 - Math.pow(1 + i, -n)) / i), 2);
}

export function annuity(L, n, i) {
  if (i === 0) return round(L / n, 2);
  return round(L * (i / (1 - Math.pow(1 + i, -n))), 2);
}

const premiumOf = (value) => Math.max(Number(value) || 0, 0);

// Life insurance duration: whole years, by default the loan term, never longer than it
const durationOf = (value, N) => Math.min(Math.max(Math.floor(Number(value)) || N, 1), N);

// Insurances and the bank account fee, normalised to { key, premium, frequency, durationYears }
function costItems(insurances, params, N) {
  const items = [];
  const { lifeBorrower, lifeCoBorrower, fire } = insurances;

  if (lifeBorrower) {
    items.push({
      key: "lifeBorrower",
      premium: premiumOf(lifeBorrower.premium),
      frequency: lifeBorrower.frequency,
      durationYears: durationOf(lifeBorrower.durationYears, N),
    });
  }
  if (lifeCoBorrower) {
    items.push({
      key: "lifeCoBorrower",
      premium: premiumOf(lifeCoBorrower.premium),
      frequency: lifeCoBorrower.frequency,
      durationYears: durationOf(lifeCoBorrower.durationYears, N),
    });
  }
  if (fire) {
    items.push({
      key: "fire",
      premium: premiumOf(fire.premium),
      frequency: fire.frequency,
      durationYears: N,
    });
  }
  items.push({
    key: "bankAccountFee",
    premium: params.bankAccountFee.premium,
    frequency: params.bankAccountFee.frequency,
    durationYears: N,
  });

  return items.filter((item) => item.premium !== 0);
}

// Steps 2–9, starting from a loan amount L
export function simulateFromLoanAmount({
  loanAmount: L,
  termYears: N,
  region,
  insurances = {},
  params = BELGIUM_LOAN_PARAMS,
}) {
  const n = 12 * N;
  const i = params.monthlyInterestRate;
  const tariff = latestTariff(params);
  const regionRules = REGIONS[region];

  const A = annuity(L, n, i);
  const annualInterestRate = Math.pow(1 + i, 12) - 1;

  // Step 2 – mortgage registration amount
  const accessories = Math.max(int(L * tariff.accessoriesRate), regionRules.minimumAccessories);
  const registrationAmount = L + accessories;

  // Step 3 – notary fees
  const registrationDuty = round(registrationAmount * tariff.registrationDutyRate, 2);
  const mortgageDuty = round(registrationAmount * tariff.mortgageDutyRate, 2);
  const retribution =
    registrationAmount > tariff.retributionThreshold ? tariff.retributionMax : tariff.retributionMin;
  const vat = round((tariff.administrativeFee + tariff.writingFee) * tariff.vatRate, 2);
  const notaryFees =
    registrationDuty +
    mortgageDuty +
    retribution +
    tariff.administrativeFee +
    tariff.writingFee +
    vat +
    tariff.annexFee;

  // Step 4 – regional contribution (not rounded)
  const regionalContribution = i === 0 ? 0 : regionRules.regionalContributionRate * L;

  // Steps 5 & 6 – one-off and periodic costs
  const items = costItems(insurances, params, N);
  const oneOffItems = items.filter((item) => item.frequency === "one-off");
  const periodicItems = items
    .filter((item) => item.frequency !== "one-off")
    .map((item) => {
      const p = FREQUENCY_MONTHS[item.frequency];
      return { ...item, intervalMonths: p, total: item.premium * item.durationYears * (12 / p) };
    });

  const oneOffPremiums = oneOffItems.reduce((sum, item) => sum + item.premium, 0);
  const oneOffCosts =
    params.valuationFee +
    params.fileFee +
    params.extraCosts +
    notaryFees +
    regionalContribution +
    oneOffPremiums;
  const periodicCosts = periodicItems.reduce((sum, item) => sum + item.total, 0);

  // Costs placed month by month (for the TAEG)
  const F0 = oneOffCosts + periodicItems.reduce((sum, item) => sum + item.premium, 0);
  const F = new Array(n + 1).fill(0);
  for (let k = 1; k <= n; k++) {
    for (const item of periodicItems) {
      if (k % item.intervalMonths === 0 && k < 12 * item.durationYears) F[k] += item.premium;
    }
  }

  // Step 7 – repayment schedule
  const schedule = [];
  let balance = L;
  for (let k = 1; k <= n; k++) {
    let capital;
    let interest;
    let instalment = A;
    if (i === 0) {
      capital = k === n ? balance : A;
      interest = 0;
      instalment = capital;
    } else {
      capital =
        k === n || balance - A < 2 ? balance : round(A * Math.pow(1 + i, -(n - k + 1)), 2);
      interest = round(A - capital, 2);
    }
    schedule.push({ month: k, balance, capital, interest, instalment, costs: F[k] });
    balance = round(balance - capital, 2);
  }

  // Step 8 – TAEG
  const I0 = round((L * i) / (365 / 12), 2);
  const firstInstalment = schedule[0].capital + I0;
  const t = Array.from({ length: n + 1 }, (_, k) => rd9((k - 1) / 12 + 1 / 365));
  const s = Array.from({ length: n + 1 }, (_, k) => rd9(k / 12));

  const f = (T) => {
    let value = L - F0 - rd9(firstInstalment / Math.pow(1 + T, t[1]));
    for (let k = 2; k <= n; k++) {
      value -= rd9(schedule[k - 1].instalment / Math.pow(1 + T, t[k]));
    }
    for (let k = 1; k <= n; k++) {
      if (F[k] !== 0) value -= rd9(F[k] / Math.pow(1 + T, s[k]));
    }
    return value;
  };

  let lo = -0.5;
  let hi = 1;
  while (f(hi) < 0 && hi < 1e6) hi *= 2;
  while (hi - lo >= 1e-12) {
    const mid = (lo + hi) / 2;
    if (f(mid) < 0) lo = mid;
    else hi = mid;
  }
  const taeg = (lo + hi) / 2;

  // Step 9 – totals
  const totalInterest = round(
    schedule.reduce((sum, row) => sum + row.interest, 0),
    2
  );
  const totalCosts = oneOffCosts + periodicCosts;
  const totalPayable = L + totalInterest + totalCosts;

  return {
    loanAmount: L,
    instalment: A,
    termYears: N,
    termMonths: n,
    monthlyInterestRate: i,
    annualInterestRate,
    accessories,
    registrationAmount,
    notary: {
      registrationDuty,
      mortgageDuty,
      retribution,
      administrativeFee: tariff.administrativeFee,
      writingFee: tariff.writingFee,
      vat,
      annexFee: tariff.annexFee,
      total: notaryFees,
    },
    regionalContribution,
    valuationFee: params.valuationFee,
    fileFee: params.fileFee,
    extraCosts: params.extraCosts,
    oneOffItems,
    oneOffCosts,
    periodicItems,
    periodicCosts,
    costsAtSigning: F0,
    oneDayInterest: I0,
    taeg,
    totalInterest,
    totalCosts,
    totalPayable,
    schedule,
    hasInsurance: items.some((item) => item.key !== "bankAccountFee"),
    parameterVersion: params.version,
  };
}

// Full simulation: the user enters the instalment they wish to pay
export function simulateLoanBelgium({ instalment, termYears, region, insurances, params = BELGIUM_LOAN_PARAMS }) {
  const P = Number(instalment);
  const N = Number(termYears);
  if (!(P > 0) || !Number.isInteger(N) || N < MIN_TERM_YEARS || N > MAX_TERM_YEARS || !REGIONS[region]) {
    return null;
  }
  const L = borrowingCapacity(P, 12 * N, params.monthlyInterestRate);
  return simulateFromLoanAmount({ loanAmount: L, termYears: N, region, insurances, params });
}
